import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useResendContractEnvelopeSignatureEmails } from '@/features/privateCompanyLeadsFunnel/hooks/useResendContractEnvelopeSignatureEmails'
import { useQueryClient } from '@tanstack/react-query'
import { Redo2Icon } from 'lucide-react'
import { Dispatch, SetStateAction, useState } from 'react'

interface ModalResendContractSignatureEmailsProps {
  privateCompanyLeadId: string
  setMessages: Dispatch<SetStateAction<string[] | boolean>>
}

export const ModalResendContractSignatureEmails = ({
  privateCompanyLeadId,
  setMessages,
}: ModalResendContractSignatureEmailsProps) => {
  const queryClient = useQueryClient()

  const { getUserPermission } = useUserPermissions()

  const userHasPermissionToResendContractSignatureEmails = getUserPermission(
    'Admin::Funnel::PrivateCompanyLeads::ResendContractSignatureEmails',
  )?.properties?.permitted_states?.includes('quoting_finalization')

  const [isOpen, setIsOpen] = useState(false)

  const onSuccess = () => {
    setIsOpen(false)
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLeadId] })
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLeadContractEnvelope', privateCompanyLeadId] })
  }

  const { mutate: resendContractSignatureEmails, isPending: resendContractSignatureEmailsIsPending } =
    useResendContractEnvelopeSignatureEmails({
      onSuccess,
      onError: FormTransformers.errorsTransformer({ setMessages }),
    })

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      Trigger={
        <Button
          type="button"
          disabled={!userHasPermissionToResendContractSignatureEmails}
          tooltipMessage={!userHasPermissionToResendContractSignatureEmails ? 'Você não tem permissão' : undefined}
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-x-2"
        >
          <p className="text-sm">Reenviar e-mails para assinatura</p>

          <Redo2Icon className="size-4" />
        </Button>
      }
      useCancellableFooter
      showClose={false}
      title="Você tem certeza?"
      titleClass="text-2xl"
      confirmButtonText="Reenviar e-mails"
      cancelButtonClassName="text-sm"
      confirmButtonClassName="text-sm bg-primary-blue"
      confirmButtonIsLoading={resendContractSignatureEmailsIsPending}
      onCancel={() => setIsOpen(false)}
      onConfirm={() => resendContractSignatureEmails(privateCompanyLeadId)}
    />
  )
}
